import axios from "axios";
import appConfig from "@/../config/config.json";
import { pathOf } from "@/composables/useRouteHelper";
import storage from "@/utils/storageUtil";

/**
 * ============================== IMPACT FE → PYTHON API ==============================
 * The Go `/ajax` backend has been fully ported to the Python FastAPI service and removed;
 * everything the FE calls targets the Python API (`appConfig.pythonApiUrl`) with JWT/Bearer auth.
 *
 * - Actions listed in MIGRATED_ENDPOINTS below (reads + mutations) are routed to the Python API
 *   with their paths/verbs. Reads keep their query params; mutations send `data` as a JSON body.
 *   A few mutations take query params on non-GET verbs and are handled inline (deleteTerm,
 *   setDefaultTerm, setDefaultTermToFalse, repollNews, reparse). Errors are real HTTP codes
 *   (409/422/404) with `{"detail": ...}` bodies.
 * - reparse is submit-then-poll (POST /reparse -> poll GET /reparse/status -> re-fetch the
 *   card's timeline) and repollNews is POST /news/repoll?ntid= — handled inline below.
 * - login/google_login/logout: POST /user/token, GET /user/google_login, POST /user/logout.
 *   The JWT is stored via the storage util under the `impactApiToken` key and attached as
 *   `Authorization: Bearer <token>` on every Python API request; a 401 clears it and redirects
 *   to login (unless already there). The one cookie left is the API's HttpOnly `impact_session`,
 *   read only by the `/assets/*` routes (screenshot iframe / open-in-new-tab cannot send a header);
 *   `syncSessionCookie` asks the API to set it from the stored JWT on every page load (DM-10602).
 *
 * Authoritative endpoint contract: impact-python/docs/md/fe-endpoint-mapping.md
 * FE-side summary: md/MIGRATION.md
 * ==================================================================================================
 */
const TOKEN_STORAGE_KEY = "impactApiToken";

/**
 * FE action name -> Python API route, applied by callApi to every matching Python API call. Reads (GET)
 * keep their query params as-is (only the path changes) and return wire-identical response
 * shapes. Mutations (non-GET) get their `data` sent as a JSON body by callApi.
 */
const MIGRATED_ENDPOINTS = {
  // clients
  getMyClients: { method: "GET", path: "clients/mine" },
  getClients: { method: "GET", path: "clients" },
  getClientsByName: { method: "GET", path: "clients/by_name" },
  getClientsUserOwns: { method: "GET", path: "clients/owned" },
  getMaxOwnedClients: { method: "GET", path: "clients/max_owned" },
  // countries
  getAllCountries: { method: "GET", path: "countries" },
  getCountryCode: { method: "GET", path: "countries/code" },
  // terms
  getTerms: { method: "GET", path: "terms" },
  getTermsLight: { method: "GET", path: "terms/light" },
  getTerm: { method: "GET", path: "terms/term" },
  getTermClient: { method: "GET", path: "terms/client" },
  getTermsSearch: { method: "GET", path: "terms/search" },
  getTermVersions: { method: "GET", path: "terms/versions" },
  getDefaultTerm: { method: "GET", path: "terms/default" },
  getDefaultLocationCtid: { method: "GET", path: "terms/default_location_ctid" },
  getCtid: { method: "GET", path: "terms/ctid" },
  getMatching: { method: "GET", path: "terms/matching" },
  // peer groups
  getPeerGroups: { method: "GET", path: "peergroups" },
  getPeerGroupByClientTerm: { method: "GET", path: "peergroups/by_client_term" },
  getPeerGroupAnalysis: { method: "GET", path: "peergroups/analysis" },
  // iresults
  getIresultSets: { method: "GET", path: "iresults/sets" },
  getIresultSet: { method: "GET", path: "iresults/set" },
  getIresultRecommendations: { method: "GET", path: "iresults/recommendations" },
  // notifications / reports
  getUserReports: { method: "GET", path: "notifications/user_reports" },
  getUserSubscriptions: { method: "GET", path: "notifications/user_subscriptions" },
  getReportTypes: { method: "GET", path: "notifications/report_types" },
  getNotificationMediums: { method: "GET", path: "notifications/mediums" },
  getUserImpactNotification: { method: "GET", path: "notifications/user_impact_notification" },
  getSentimentReport: { method: "GET", path: "notifications/sentiment_report" },
  // misc
  getResultTypes: { method: "GET", path: "result_types" },
  getCategoryTags: { method: "GET", path: "category_tags" },
  getNewsStatus: { method: "GET", path: "news/status" },
  getFavicon: { method: "GET", path: "favicon" },
  getGeoUrl: { method: "GET", path: "geo_url" },
  getReleaseLabel: { method: "GET", path: "release_label" },
  getUsers: { method: "GET", path: "users" },
  getUserInfo: { method: "GET", path: "user/info" },
  // assets / timeline / results — previously still-on-Go, now Python. Reads keep their query
  // params as-is (extra Go-only params like clientid/color are ignored by the Python routes).
  serveHTML: { method: "GET", path: "assets/serp_html" }, // getKpHtml
  getFeaturedResultsScreenshot: { method: "GET", path: "assets/featured_results_screenshot" },
  getReportImageBase64: { method: "GET", path: "assets/report_image" },
  getMarkup: { method: "GET", path: "markup" },
  getTimelineJson: { method: "GET", path: "timeline" }, // getTimeline (Python /timeline reads Mongo directly)
  getTermRange: { method: "GET", path: "timeline/term_range" },
  getDetails: { method: "GET", path: "timeline/details" }, // getSuggestions
  getTimelineHtml: { method: "GET", path: "timeline/html" }, // DM-10753: AI-overview / PAA html, per poll
  getResults: { method: "POST", path: "timeline/export" }, // postResults; text/csv, body clientid access-checked
  // getTermDropDown — NEW backend route (another agent is adding it). Returns JSON (was HTML on Go);
  // the timeline-toolbar component that consumes it is owned by another agent — see response-shape note.
  getTermDropDown: { method: "GET", path: "terms/dropdown" },
  // charts — unified chart data (getChartsDataV2). All six FE chart methods
  // (getChartHistory/Location/PeerGroup + getFrequency/Locations/Peers) route
  // through createChartsDataCall -> callApi("charts/data", ...); the groupBy /
  // dataMode discriminators travel as query params. Python response JSON matches
  // Go's chartutil.ChartDataResponse shape, so the chart composables are unchanged.
  "charts/data": { method: "GET", path: "charts/data" },
  // ---- mutations (landed) ----
  // iresults writes — success is Go SendOk(WithData) parity:
  // {"status":"ok"[,"data":"<irid>"],"updated":"<RFC3339>"} (data omitted when empty).
  saveIresult: { method: "PUT", path: "iresults/set" },
  saveIresultSet: { method: "PUT", path: "iresults/sets" },
  renameIresultSet: { method: "POST", path: "iresults/set/rename" }, // was GET query params; now JSON {irid, name}
  deleteIresultSet: { method: "DELETE", path: "iresults/set" }, // verb change PUT -> DELETE; JSON {id}
  // notifications writes (SU-only group) — success {"status":"ok"}; 409 already-exists, 404 unknown
  upsertReportSetting: { method: "POST", path: "notifications/report_setting" }, // verb change PUT -> POST
  deleteReportSetting: { method: "DELETE", path: "notifications/report_setting" }, // verb change PUT -> DELETE
  subscribeUserForReportSetting: { method: "POST", path: "notifications/report_setting/subscribe" },
  unsubscribeUserForReportSetting: { method: "POST", path: "notifications/report_setting/unsubscribe" },
  dismissUserImpactNotification: { method: "POST", path: "notifications/user_impact_notification/dismiss" },
  // auth / password reset (pre-auth) — success {"status":"ok"}; 422 expired code / short password
  sendPasswordResetEmail: { method: "POST", path: "user/send_password_reset_email" }, // JSON {email} (extra isApp key is ignored)
  resetPassword: { method: "POST", path: "user/reset_password" }, // JSON {code, password}
  // ---- markup writes (markup_router.py) ----
  // All three return the markup map {"<urlid>": {rating, owned, auth,...}} exactly like Go's
  // timeline.MarkupMap (owned/auth STRINGS, rating -4 stripped) — response consumption unchanged.
  // The Go form/query payloads become JSON bodies (extra start/end/ctid/resultset keys are
  // ignored by the Pydantic schemas). Ownership/validation conflicts that Go returned as
  // 200-{status:error} are now real 409/422/403 rejections.
  rateURL: { method: "POST", path: "markup/rate" }, // was GET form; now JSON body (score/urlid/ts/action/allowOverwriteHistory/skipOwnershipCheck/clientid/resultType)
  ownURL: { method: "POST", path: "markup/own" }, // already JSON POST; body key is already `urlids` (possiblyAffectedUrlIds)
  rateQuestion: { method: "POST", path: "markup/rate-question" }, // DM-10271: {question, score, clientid}; resp {question, rating}
  setAuthority: { method: "POST", path: "markup/authority" }, // was GET form; now JSON body; non-iauthority values are SU-only (403)
  "analysis/urls/accept": { method: "PUT", path: "analysis/urls/accept" }, // acceptAllAnalysis — same path/verb; JSON {clientid, data}; resp is dict[int,int]
  // ---- client/term admin (admin_router.py) ----
  updateClient: { method: "POST", path: "clients/upsert" }, // JSON body 1:1 (clientid:-1 = create); resp bare int client id (success path ignores it)
  updateTerm: { method: "POST", path: "terms/update" }, // stays FORM-urlencoded (router reads Form(...)); resp is JSON array of added tids
  // deleteTerm / setDefaultTerm / setDefaultTermToFalse take QUERY params on non-GET verbs, so
  // they can't ride the JSON-body table — they are handled inline in their api fns below.
};

/**
 * Base URL per service. `pythonApi` is the default for every call; `geo` is the geolocation
 * service, whose URL is fetched once from the API (`api`) and cached in `url`.
 */
let baseUrls = {
  geo: { url: undefined, api: "getGeoUrl", pathPrefix: '' },
  pythonApi: { url: appConfig.pythonApiUrl },
};

function storePythonApiToken(token) {
  storage.set(TOKEN_STORAGE_KEY, token || "", true);
}

function callApi(path, data, o = {}) {
  const defaultOptions = { method: "GET", service: "pythonApi", headers: {} };
  o = Object.assign({}, defaultOptions, o);

  // Translate legacy action names to their Python route (params unchanged).
  if (o.service === "pythonApi" && MIGRATED_ENDPOINTS[path]) {
    o.method = MIGRATED_ENDPOINTS[path].method;
    path = MIGRATED_ENDPOINTS[path].path;
  }

  return getBaseUrl(o.service).then((url) => {
    const fullPath = url + "/" + path;

    // Attach the JWT on requests that target the Python API (Bearer-only auth; no session cookie).
    if (o.service === "pythonApi") {
      const token = storage.get(TOKEN_STORAGE_KEY);
      if (token) {
        o.headers = Object.assign({}, o.headers, { Authorization: `Bearer ${token}` });
      }
    }

    let call = null;
    if (o.method === "GET") {
      const queryString = fullPath + (data ? "?" + new URLSearchParams(data).toString() : "");
      const limit = 2000;
      if (queryString.length >= limit) {
        // This is enforced by the security layer. Adding here so we catch it earlier.
        return Promise.reject("Query string too long in API request");
      }
      // axios.get's second arg is a config object, so headers must be nested under `headers`
      call = axios.get(queryString, { headers: o.headers, withCredentials: !!o.withCredentials });
    } else {
      call = axios({ method: o.method, url: fullPath, data, headers: o.headers, withCredentials: !!o.withCredentials });
    }

    return call.catch((e) => {
      if (e.response && e.response.status === 401 && !location.href.includes('/login')) {
          storePythonApiToken(""); // token is missing/expired/invalid - clear it so login refreshes it
          storage.set('intendedRoute', location.href, true);
          location.href = pathOf('/login');
      }
      throw e;
    });
  });
}

function getBaseUrl(service) {
  const target = baseUrls[service];
  if (!target) {
    return Promise.reject(`unknown service ${service}`);
  }

  // previously set - just return it
  if (target.url) {
    return Promise.resolve(target.url);
  }

  // if this is the first time using this base URL, get and set it now
  // TODO this nested recursive call works for now but probably a bad idea to leave this way
  return callApi(target.api)
    .then((response) => (target.url = response.data + target.pathPrefix))
    .catch((err) => {
      console.error(`Error getting ${service} base url`, err);
      return Promise.reject(err);
    });
}

// This shouldn't be necessary but see DM-2136
function removeDuplicatesFromTermRange(response) {
  const uniqueTids = {};
  const uniqueTerms = [];

  response.data.forEach((o) => {
    const key = o.term.tid + "|" + o.date;
    if (!uniqueTids[key]) {
      uniqueTids[key] = true;
      uniqueTerms.push(o);
    }
  });

  response.data = uniqueTerms;
  return response;
}

/**
 * Find the keys that contain the url and markup. The timeline JSON structure uses non-standard
 * key names (names depend on result type). Also, sometimes details are nested in an array.
 * Needed so far by markup and outlining. (This should really be done by the API. See DM-4381)
 *
 * TODO move resultType fixes and iskp property here too
 */
function initTimelineForFindingResultDetails(response) {
  const isRegularDetailSection = (value) => typeof value === "object" && value.markup && value.url;
  const isContainerDetailSection = (value) => Array.isArray(value) && isRegularDetailSection(value[0]);

  function loadDetailSection(result) {
    let regular, container;

    // Trying to migrate away from these type-specific keys so we don't need this whole method anymore. So far the news feed uses proper generic key names.
    if (result.containerDetailSections) {
      container = result.containerDetailSections;
    }
    else if (result.detailSection) {
      regular = result.detailSection;
    }
    else {
      for (const [key, value] of Object.entries(result)) {
        // A minisitelinksbox also carries its child links (DM-10632); the timeline shows only the box.
        if (key === "minisitelink") {
          continue;
        }

        if (isRegularDetailSection(value)) {
          regular = value;
          continue;
        }

        if (isContainerDetailSection(value)) {
          container = value;
          continue;
        }

        if (typeof value === "object" && value.image && isContainerDetailSection(value.image)) {
          container = value.image;
          continue;
        }
      }
    }

    result.computed ||= {};

    // sometimes containers also have a regular detail section
    if (container) {
      result.computed.containerDetailSections = container;
    } else if (regular) {
      result.computed.detailSection = regular;
    }
  }

  // TODO consider flattening the data into one level to make this simpler
  (response.data || []).forEach((card) => {
    card.result = card.result.filter(isRelevantResult);
    // do this in a second step so sitelink logic below sees only the relevant results
    card.result = card.result.map((result, index) => {

      // handle featureresultspanel
      if (result.resulttype === 'featuredresultspanel') {
        processFeaturedResultspanel(result);
      }

      loadDetailSection(result);

      if (result.resulttype === "sitelinkbox" && result.sitelink) {
        if (card.result[index - 1].standard) {
          result.siteLinkParent = card.result[index - 1].standard;
          card.result[index - 1].computed.hasSiteLinks = true;
        } else {
          // TODO maybe do this as part of isRelevantResult. Or better yet, all this should be in the API.
          console.warn("IMPACT: Found a sitelink without a parent");
          console.warn({ result });
          return null;
        }
      }

      return result;
    })
      .filter((result) => result);
  });

  return response;
}

function processFeaturedResultspanel(result) {
  const featuredResultTypes = [
    'featuredresultsdatacard',
    'featuredresultsimage',
    'featuredresultsvideo',
    'featuredresultswebsite',
    'featuredresultssocial',
    'featuredresultsgeneral'
  ];

  // Extract all elements from the arrays and store them in a new array
  const featuredResultsData = featuredResultTypes.reduce((acc, type) => {
    if (result.featuredresultspanel[type]) {
      acc.push(...result.featuredresultspanel[type]);
    }
    return acc;
  }, []);

  // Remove unnecessary attributes
  featuredResultTypes.forEach(type => {
    delete result.featuredresultspanel[type];
  });

  // Move 'css' and 'featuredresultsthumbnail' attributes to 'result' and remove them from 'featuredresultspanel'
  result.css = result.featuredresultspanel.css;
  delete result.featuredresultspanel.css;

  result.featuredresultsthumbnail = result.featuredresultspanel.featuredresultsthumbnail;
  delete result.featuredresultspanel.featuredresultsthumbnail;

  // Assign the new array to 'result.featuredresultspanel'
  result.featuredresultspanel = featuredResultsData;
}

function isRelevantResult(result) {
  return !(
    result.resulttype === "unrecognized" ||
    (result.resulttype === "standard" && result.standard?.desc === "Shopping ideas")
  );
}

// Remove unrecongnized elements that used to be included in cards and may have crept into iresults.
// We can delete this soon when all iresult sets don't have irrelevant results.
function cleanOldIresultSet(response) {
  response.data.Result = response.data.Result.filter(isRelevantResult);
  return response;
}

/**
 * ---- reparse (flag-on) submit-then-poll ----
 * Go's /ajax/reparse long-polled server-side for up to 150s and returned the refreshed
 * timeline inline. The Python API splits this into POST /reparse (202 {pollid, status})
 * + GET /reparse/status?googlepollid=; the FE polls every 2s until "success"/"failed",
 * with a client-side give-up (local timer) mirroring Go's old 150s ceiling. The status
 * response also carries created_at, but a local timer avoids client/server clock skew.
 */
const REPARSE_POLL_INTERVAL_MS = 2 * 1000;
const REPARSE_GIVE_UP_MS = 150 * 1000;

function pollReparseStatus(googlepollid) {
  const deadline = Date.now() + REPARSE_GIVE_UP_MS;

  const poll = () =>
    callApi("reparse/status", { googlepollid }).then((res) => {
      const status = res.data && res.data.status;
      if (status === "success") {
        return res;
      }
      if (status === "failed") {
        return Promise.reject(new Error(res.data.error_message || "Reparse failed"));
      }
      if (Date.now() >= deadline) {
        return Promise.reject(new Error(`Reparse still pending after ${REPARSE_GIVE_UP_MS / 1000}s`));
      }
      return new Promise((resolve) => setTimeout(resolve, REPARSE_POLL_INTERVAL_MS)).then(poll);
    });

  return poll();
}

const createChartsDataCall = (groupBy, dataMode) =>
  (ctid, start, end, clientdetailid) => {
    const params = { ctid, start, end, groupBy, dataMode };
    if (clientdetailid) params.clientid = clientdetailid;
    return callApi("charts/data", params);
  };


export default {
  deleteIresultSet: (id) => callApi("deleteIresultSet", { id }, { method: "PUT" }),

  deleteReportSetting: (report_setting_id) => callApi("deleteReportSetting", { report_setting_id }, { method: "PUT" }),

  // DELETE /terms with term/tids/clientid as QUERY params (router reads them via Query, not a
  // body) — same inline pattern as repollNews. Success {"status":"ok"} (ignored by the caller).
  deleteTerm: (term, clientid, tids) =>
    callApi("terms?" + new URLSearchParams({ term, clientid, tids }).toString(), null, { method: "DELETE" }),

  dismissUserImpactNotification: (report_setting_id, user_subscription_id) => callApi("dismissUserImpactNotification", { report_setting_id, user_subscription_id }),

  getAllCountries: () => callApi("getAllCountries"),

  getCountryCode: (pollCountry) => callApi("getCountryCode", { pollCountry }),

  getCategoryTags: () => callApi("getCategoryTags"),
  
  getCtid: (clientid, tid) => callApi("getCtid", { clientid, tid }),

  getFavicon: (domainUrl) => callApi("getFavicon", { domainUrl }),

  getGeoAutoComplete: (text) => callApi(`autoComplete/${text}`, {}, { service: 'geo' }),

  getIresultRecommendations: () => callApi("getIresultRecommendations"),

  getIresultSet: (clientid, id, ctid) => callApi("getIresultSet", { clientid, id, ctid }).then(cleanOldIresultSet),

  getIresultSets: (clientid, ctid) => callApi("getIresultSets", { clientid, ctid }),

  getKpHtml: (clientid, tid, date, page, color, modify) => callApi("serveHTML", { clientid, tid, date, page, color, modify }),

  getMarkup: (clientid, url, ts = null) => callApi("getMarkup", { clientid, url, ts: ts || Math.round(Date.now() / 1000) }),

  getMatchingClient: (clientid, filter, activeonly, filtertype) => callApi("getClients", { clientid, filter, activeonly, filtertype }),

  getClientsByName: (clientid, clientnamehas) => callApi("getClientsByName", {clientid, clientnamehas}),

  getMatchingClientTerm: (str) => callApi("getMatching", { str }),

  getMaxOwnedClients: () => callApi("getMaxOwnedClients"),

  getMyClients: (clientid) => callApi("getMyClients", { clientid }),

  getClientsUserOwns: (clientid) => callApi("getClientsUserOwns", { clientid }),

  getUsers: () => callApi("getUsers", {  }),

  getNewsStatus: (clientid) => callApi("getNewsStatus", { clientid }),

  getNotificationMediums: () => callApi("getNotificationMediums"),

  getPeerGroupAnalysis: (id, startdate, enddate) => callApi("getPeerGroupAnalysis", { id, startdate, enddate }),

  getPeerGroupByClientTerm: (clientid, tid) => callApi("getPeerGroupByClientTerm", { clientid, tid }),

  getPeerGroups: (clientid) => callApi("getPeerGroups", { clientid }),

  getReleaseLabel: () => process.env.NODE_ENV === "production" ? callApi("getReleaseLabel") : Promise.resolve({ data: "dev" }),

  getReportImageBase64: (width, height, url) => {
    // TODO handle this better
    if (process.env.NODE_ENV !== "production") { url = url.replace("localhost:8082", "localhost:4000"); }

    return callApi("getReportImageBase64", { width, height, url });
  },

  getReportTypes: () => callApi("getReportTypes"),

  // TODO: I think the paa_only param is always false
  postResults: (data) => callApi("getResults", data, { method: "POST" }),

  getResultTypes: () => callApi("getResultTypes"),

  getSentimentReport: (userid, date) => callApi("getSentimentReport", { userid, date }),

  getSuggestions: (googlepollid, urlid, ctid, clientid) => callApi("getDetails", { googlepollid, urlid, ctid, clientid }),

  // DM-10753: the html /timeline flags with `hasHtml` — use utils/resultHtml.loadResultHtml, which caches it per poll
  getTimelineHtml: (googlepollid, clientid) => callApi("getTimelineHtml", { googlepollid, clientid }),

  getTerm: (clientid, ctid) => callApi("getTerm", { clientid, ctid }),

  getTermClient: (ctid) => callApi("getTermClient", { ctid }),

  getTermDropDown: (clientid, ctid, resultset) => callApi("getTermDropDown", { clientid, ctid, resultset }),

  getAllTerms: (clientid) => callApi("getTerms", { clientid }),

  getTermRange: (clientid, ctid, start, end, resultset) => callApi("getTermRange", { clientid, ctid, start, end, resultset }).then(removeDuplicatesFromTermRange),

  getTermsLight: (clientid) => callApi("getTermsLight", { clientid }),

  getTermsSearch: (clientid, pattern) => callApi("getTermsSearch", { clientid, pattern }),

  getTermVersions: (clientid, term) => callApi("getTermVersions", { clientid, term }),

  // Always routes to Python /timeline (via the getTimelineJson entry in MIGRATED_ENDPOINTS).
  // DM-9915 (revives the DM-1142 toolbar switch): `mongoView` is false/true while the superuser switch
  // is showing (forces MySql/Mongo via `source`, superuser-only server-side) and null while it is
  // hidden — then no `source` is sent and the server applies its own per-poll app.results_mongo_from
  // cutover. The Go-era mongo redirect this switch used to drive is gone; `source` replaces it.
  getTimeline: (clientid, ctid, start, end, resultset, mongoView = null) => callApi("getTimelineJson", {
    clientid, ctid, start, end, resultset,
    ...(mongoView === true || mongoView === false ? { source: mongoView ? "mongo" : "mysql" } : {}),
  }).then(initTimelineForFindingResultDetails),

  getNewsTimeline: (clientid, ctid, start, end, resultset) => callApi("news/timeline", { clientid, ctid, start, end, resultset })
    .then((response) => {
      // API now (DM-7224) returns wrapper: { results: [...], emptyReason: "..." }
      // Transform so initTimelineForFindingResultDetails can process results
      // TODO: consider making the timeline response structures consistent
      const wrapper = response.data || {};
      response.data = wrapper.results || [];
      response.emptyReason = wrapper.emptyReason || null;
      return response;
    })
    .then(initTimelineForFindingResultDetails),

  getAnalysisData: (clientid, urls, source) => callApi("analysis/urls/query", { clientids: clientid, urls, source }, { method: "GET" }),

  analyzeNewsCard: (clientid, ntid, date, location, country) => callApi("analysis/news_card/urls", { clientid, ntid, start: date, location, country }, { method: "POST" }),

  analyzeNewsTimeline: (clientid, ctid, start, end, resultset) => callApi("analysis/news_timeline/urls", { clientid, ctid, start, end, resultset }, { method: "POST" }),

  // getNewsTimeline: () => Promise.resolve({ data: newsTimelineTestData }).then(initTimelineForFindingResultDetails),

  // getAnalysisData: () => Promise.resolve({ data: analysisTestData }),

  // analyzeNewsCard: () => Promise.resolve({ data: analyzeNewsCardTestData }),

  // analyzeNewsTimeline: () => Promise.resolve({ data: analyzeNewsCardTestData }),

  acceptAllAnalysis: (clientid, valueMap) => callApi("analysis/urls/accept", { clientid, data: valueMap }, { method: "PUT" }),

  getDefaultLocationCtid: (polllocation, pollcountry, clientid) => callApi("getDefaultLocationCtid", { polllocation, pollcountry, clientid }),

  getUserImpactNotification: (userid) => callApi("getUserImpactNotification", { userid }),

  getUserInfo: (clientid) => callApi("getUserInfo", { clientid }),

  getUserReports: (userid) => callApi("getUserReports", { userid }),

  getUserSubscriptions: () => callApi("getUserSubscriptions"),

  getFeaturedResultsScreenshot: (tid, date) => callApi("getFeaturedResultsScreenshot", { tid, date }),

  // Python-only login (Go removed). POST /user/token returns {access_token, ...}; we store the
  // JWT and adapt the return so Login.vue's handleLoginResponse (which only checks
  // res.data.status === "ok") still works. On failure the callApi promise rejects and Login.vue
  // surfaces the error. RESPONSE-SHAPE CHANGE: returns a synthesized {data:{status:"ok",...}}
  // wrapper (Go used to return {status:"ok"} directly); the extra Python fields (access_token,
  // user_first_name, user_last_name, user_id) are spread in but no current caller reads them.
  login: (email, password) =>
    callApi("user/token", { username: email, password, remember_me: true }, { method: "POST" })
      .then((res) => {
        storePythonApiToken(res.data && res.data.access_token);
        return { data: { status: "ok", ...res.data } };
      }),

  // MCP OAuth consent flow (see views/Authorize.vue): the consent page sends the
  // dashboard's own login JWT to the MCP server, so it only needs a login check
  // (401 -> the interceptor bounces to /login) and the stored token itself.
  checkLogin: () => callApi("user/check_login"),
  getPythonApiToken: () => storage.get(TOKEN_STORAGE_KEY),

  // Google login - Python-only (GET /user/google_login?token=). Same store-token + status:"ok"
  // adaptation as login above.
  tokenlogin: (token) =>
    callApi("user/google_login", { token })
      .then((res) => {
        storePythonApiToken(res.data && res.data.access_token);
        return { data: { status: "ok", ...res.data } };
      }),

  // The /assets routes (screenshot iframe, open-in-new-tab) cannot send the Bearer header, so the API
  // sets an HttpOnly cookie from it instead of the FE putting the JWT in the URL (DM-10602). Credentialed
  // so the browser keeps the cookie for the API host, which is a different host of the same site as the
  // dashboard. Best-effort: without the cookie, screenshots 401 but nothing else does.
  syncSessionCookie: () =>
    callApi("user/session_cookie", null, { method: "POST", withCredentials: true })
      .catch((e) => console.error("IMPACT: could not set the session cookie", e)),

  // Python-only logout (POST /user/logout, Bearer attached by callApi). Best-effort — the JWT is
  // stateless, so failures are only logged; the stored token is always cleared afterward. Credentialed
  // so the response can clear the `impact_session` cookie.
  logout: () =>
    callApi("user/logout", null, { method: "POST", withCredentials: true })
      .catch((e) => console.error("IMPACT: Python API logout failed", e))
      .finally(() => storePythonApiToken("")),

  ownURL: (clientid, owned, urlid, possiblyAffectedUrlIds, ts, start, end, ctid, resultset, resultType) => callApi("ownURL", { clientid, owned, urlid, ts, urlids: possiblyAffectedUrlIds, start, end, ctid, resultset, resultType }, { method: "POST" }),

  updateClient: (data) => callApi("updateClient", data, {method: "POST"}),

  rateQuestion: (clientid, question, score) => callApi("rateQuestion", { clientid, question, score }, { method: "POST" }),

  rateURL: (clientid, score, urlid, ts, action, start, end, ctid, resultset, resultType) => callApi("rateURL", { clientid, score, urlid, ts, action, allowOverwriteHistory: true, skipOwnershipCheck: true, start, end, ctid, resultset, resultType }),

  renameIresultSet: (irid, name) => callApi("renameIresultSet", { irid, name }),

  // Submit-then-poll: POST /reparse (202) -> poll GET /reparse/status (see pollReparseStatus
  // above) -> re-fetch this card's timeline via getTimelineJson, then resolve with the shape
  // {data: [refreshed card]} so the caller (CardHeader.initReparse -> refreshCard) is unchanged.
  // `refetch` ({ctid, date, resultset}) is provided by the caller.
  reparse: (googlepollid, clientid, refetch = {}) => {
    const submitPath = "reparse" + (clientid != null ? "?" + new URLSearchParams({ clientid }).toString() : "");
    return callApi(submitPath, { googlepollid }, { method: "POST" })
      .then(() => pollReparseStatus(googlepollid))
      .then(() => callApi("getTimelineJson", { clientid, ctid: refetch.ctid, start: refetch.date, end: refetch.date, resultset: refetch.resultset }))
      .then(initTimelineForFindingResultDetails)
      .then((res) => {
        const cards = (res.data || []).filter((card) => String(card.pollid) === String(googlepollid));
        if (!cards.length) {
          return Promise.reject(new Error(`Reparsed poll ${googlepollid} not found in the refreshed timeline`));
        }
        res.data = cards;
        return res;
      });
  },

  // SU-only (news card speed dial). POST /news/repoll with ntid staying a QUERY param per the
  // contract (so it bypasses the JSON-body handling of the endpoints table). Response is
  // {"status":"ok"} — the caller only truthy-checks res.data.
  repollNews: (ntid) =>
    callApi("news/repoll?" + new URLSearchParams({ ntid }).toString(), null, { method: "POST" }),

  resetPassword: (code, password) => callApi("resetPassword", { code, password }),

  saveIresult: (id, results, siteLinks, kg) => callApi("saveIresult", { id, results, siteLinks, kg }, { method: "PUT" }),

  saveIresultSet: (name, tid, clientId, results, siteLinks, kg) => callApi("saveIresultSet", { name, tid, clientId, results, siteLinks, kg }, { method: "PUT" }),

  sendPasswordResetEmail: (email) => callApi("sendPasswordResetEmail", { email, isApp: true }),

  setAuthority: (clientid, auth, urlid, start, end, ctid, resultset, resultType) => callApi("setAuthority", { clientid, auth, urlid, start, end, ctid, resultset, resultType }),

  // POST /terms/default with tids/clientid as QUERY params (router reads Query, not a body).
  // Response is ignored.
  setDefaultTerm: (clientid, tids) =>
    callApi("terms/default?" + new URLSearchParams({ tids, clientid }).toString(), null, { method: "POST" }),

  // DELETE /terms/default with clientid as a QUERY param.
  setDefaultTermToFalse: (clientid) =>
    callApi("terms/default?" + new URLSearchParams({ clientid }).toString(), null, { method: "DELETE" }),

  getDefaultTerm: (clientid) => callApi("getDefaultTerm", { clientid }),

  subscribeUserForReportSetting: (report_setting_id, email) => callApi("subscribeUserForReportSetting", { report_setting_id, email }),

  unsubscribeUserForReportSetting: (report_setting_id, email) => callApi("unsubscribeUserForReportSetting", { report_setting_id, email }),

  updateTerm: (formData) => callApi("updateTerm", formData, { method: "POST", headers: { 'Content-Type': 'application/x-www-form-urlencoded' } }),

  upsertReportSetting: (loggedInUser, reportSettingId, selectedReportType, selectedMediums, keywordSelections) => callApi("upsertReportSetting", { loggedInUser, reportSettingId, selectedReportType, selectedMediums, keywordSelections }, { method: "PUT" }),

  // Chart Dashboard API methods (using unified charts/data endpoint)
  getChartHistory: createChartsDataCall("history", "markup"),
  getChartLocation: createChartsDataCall("location", "markup"),
  getChartPeerGroup: createChartsDataCall("peer", "markup"),

  // Frequency Chart API methods (using unified charts/data endpoint)
  getFrequency: createChartsDataCall("history", "frequency"),
  getFrequencyLocations: createChartsDataCall("location", "frequency"),
  getFrequencyPeers: createChartsDataCall("peer", "frequency"),
};


// const analyzeNewsCardTestData = [ { clientid: 3503, urlids: [87465872] } ];

// const analysisTestData = [
//   {
//     "request": {
//       "source": "news",
//       "clientInfo": {
//         "clientid": 3503,
//         "name": "Test Search Element"
//       },
//       "urlInfo": {
//         "urlid": 87465872,
//         "url": "http://www.businessinsider.com/vacation-rental-startup-savvy-poke-fun-at-airbnb-with-billboard-2025-4",
//         "title": "A little-known vacation-rental company poked fun at Airbnb with a pointed billboard",
//         "description": "Savvy, a Texas-based platform that promises no fees for hosts and guests, took aim at bigger rivals Airbnb and Vrbo with a new billboard."
//       }
//     },
//     "response": {
//       "contentLengthSent": 2788,
//       "summary": "Savvy, a new vacation rental startup, is challenging industry giants Airbnb and Vrbo with a no-fee business model, advertising via a strategic billboard placement near Vrbo's headquarters. The company, which launched in February with 150,000 properties, targets professional hosts with 5+ properties and promises no fees for hosts or guests, though it offers optional promotional subscriptions.",
//       "score": 3,
//       "rating": 0,
//       "ratingReason": "The article presents a factual account of a new market entrant challenging established players through competitive marketing tactics. While it highlights potential benefits for consumers (no fees), it also shows limitations (professional hosts only) and the company's small scale compared to competitors.",
//       "confidence": 0.8,
//       "confidenceReason": "The article provides clear facts about the company's business model, size, and strategy. However, as a new company, there's limited track record to evaluate long-term viability or impact on the market, slightly reducing confidence in the overall assessment."
//     }
//   }
// ]


// const newsTimelineTestData = [{ "_id": 0, "pollid": "678d016bca41494bb74a9011", "timezoneId": "America/New_York", "ts": "2025-01-19T13:43:04.752000", "datetime": "2025-01-19T13:43:04+00:00", "engine": "google", "device": "pc", "term": { "tid": 35600, "ntid": 11, "term": "Marcelo Garcia", "country": "USA", "location": "New York NY", "lang": "en" }, "result": [{ "resulttype": "source-news-standard", "position": 0, "page": 1, "detailSection": { "desc": "The return of legendary grappler Marcelo Garcia is set for Jan. 24 in Thailand, when he faces Japanese veteran Masakazu Imanari in a 10-minute grappling...", "title": "MSN", "url": "http://www.msn.com/en-us/sports/mma/marcelo-garcia-to-face-masakazu-imanari-in-grappling-return-at-one-170/ar-AA1vhsi1?ocid=BingNewsVerp&apiversion=v2&noservercache=1&domshim=1&renderwebcomponents=1&wcseo=1&batchservertelemetry=1&noservertelemetry=1", "urlid": 87465868, "markup": { "rating": -4, "owned": "notowned", "auth": "expert" }, "aimarkup": { "contentLengthSent": "200", "summary": "Legendary grappler Marcelo Garcia is making his return to competition at ONE 170 on January 24th in Thailand. He will face Japanese veteran Masakazu Imanari in a 10-minute grappling match, marking a significant comeback in the sport.", "score": 2, "rating": -1, "ratingReason": "The article announces the return of a highly respected figure in grappling (Marcelo Garcia) to competition, which is generally seen as positive news for combat sports fans. The matchup against another veteran competitor adds legitimacy to the event.", "confidence": 0.7, "confidenceReason": "While the article content is limited, the key information about the match announcement is clear. However, the brevity of the article and lack of additional details about fight terms, fighter conditions, or context reduces full confidence in sentiment assessment." }, "published": "11 hours ago", "publishedMinutes": 660, "image": "https://fb-serp-stream-feed-dev.s3.amazonaws.com/news/2025/01/19/11/dimg_MAGNZ7eJMuu9kPIPyPr5mAI_2.jpeg", "articleTitle": "Marcelo Garcia to face Masakazu Imanari in grappling return at ONE 170" } }, { "resulttype": "source-news-standard", "position": 0, "page": 1, "detailSection": { "desc": "BJJ legend Marcelo Garcia is no longer a stranger to proving himself as he has found himself in many such situations throughout his...", "title": "Sportskeeda", "url": "http://www.sportskeeda.com/mma/news-i-prove-that-bjj-legend-marcelo-garcia-says-always-competed-chip-shoulder", "urlid": 87465869, "markup": { "rating": 0, "ratingHistory": { "0": 0 }, "auth": "crowd" }, "aimarkup": {}, "published": "2 days ago", "publishedMinutes": 2880, "image": "https://fb-serp-stream-feed-dev.s3.amazonaws.com/news/2025/01/19/11/dimg_MAGNZ7eJMuu9kPIPyPr5mAI_15.jpeg", "articleTitle": "â€œI have to prove thatâ€ - BJJ legend Marcelo Garcia says he always competed with a chip on his shoulder" } }, { "resulttype": "source-news-standard", "position": 0, "page": 1, "detailSection": { "desc": "BJJ legend Marcelo Garcia spoke about his highly anticipated return to competition and why he chose to sign with ONE Championship in...", "title": "ONE Championship", "url": "http://www.onefc.com/news/ill-give-everything-i-have-bjj-goat-marcelo-garcia-opens-up-about-return-to-competition-in-one-championship/", "urlid": 87465870, "markup": { "rating": -4 }, "aimarkup": { "summary": "Brazilian Jiu-Jitsu legend Marcelo Garcia has announced his return to competition through ONE Championship. Garcia, widely considered the BJJ GOAT (Greatest of All Time), expressed commitment and enthusiasm about giving his maximum effort in this comeback.", "score": 1, "rating": 0, "ratingReason": "The article presents highly positive news about a legendary athlete's return to competition. The use of terms like 'GOAT' and Garcia's own quote 'I'll Give Everything I Have' indicate strong positive sentiment and commitment. His choice to sign with ONE Championship is presented as a significant positive development.", "confidence": 0.8, "confidenceReason": "While the article content is limited, the consistent positive messaging across the URL, title, and description provides clear sentiment indicators. The confidence is not 1.0 due to the limited content available and lack of detailed information about specific plans or terms of the signing." }, "published": "Nov 15, 2024", "publishedMinutes": 94542, "image": "https://fb-serp-stream-feed-dev.s3.amazonaws.com/news/2025/01/19/11/dimg_MAGNZ7eJMuu9kPIPyPr5mAI_9.jpeg", "articleTitle": "â€˜Iâ€™ll Give Everything I Haveâ€™ â€“ BJJ GOAT Marcelo Garcia Opens Up About Return To Competition In ONE Championship" } }, { "resulttype": "source-news-standard", "position": 0, "page": 1, "detailSection": { "desc": "As far as Marcelo Garcia is concerned, being a jack of all trades and master of none is detrimental to one's Brazilian jiu-jitsu journey.", "title": "Sportskeeda", "url": "http://www.sportskeeda.com/mma/news-bjj-legend-marcelo-garcia-preaches-quality-quantity-comes-techniques-you-don-t-them", "urlid": 87465871, "markup": { "rating": -4, "auth": "crowd" }, "aimarkup": { "analysisKeyword":"OOGA", "summary": "BJJ legend Marcelo Garcia advocates for focusing on mastering specific techniques rather than trying to learn everything in Brazilian jiu-jitsu. He emphasizes that specialization and quality of technique execution is more valuable than attempting to learn every possible move.", "score": 2, "rating": 1, "ratingReason": "The message promotes a focused, quality-driven approach to martial arts training which aligns with effective learning principles. Garcia's guidance simplifies the learning process and reduces overwhelming complexity for practitioners.", "confidence": 0.8, "confidenceReason": "While the article is brief, the message is clear and comes from a highly respected authority in BJJ. The title and description consistently convey the same focused message, though more detailed content would provide higher confidence." }, "published": "2 days ago", "publishedMinutes": 2880, "image": "https://fb-serp-stream-feed-dev.s3.amazonaws.com/news/2025/01/19/11/dimg_MAGNZ7eJMuu9kPIPyPr5mAI_17.jpeg", "articleTitle": "BJJ legend Marcelo Garcia preaches quality over quantity when it comes to techniques: â€œYou donâ€™t have to do all of themâ€" } }, { "resulttype": "source-news-standard", "position": 0, "page": 1, "detailSection": { "desc": "Marcelo Garcia is widely regarded as the greatest pound-for-pound Brazilian Jiu-Jitsu competitor the sport has ever seen. Born and raised in the Brazilian...", "title": "ONE Championship", "url": "http://www.onefc.com/athletes/marcelo-garcia", "urlid": 87460499, "markup": { "rating": -4 }, "aimarkup": { "analysisKeyword":"OOGA", "summary": "Marcelo Garcia is profiled as one of the most accomplished Brazilian Jiu-Jitsu practitioners in history, recognized as potentially the greatest pound-for-pound competitor the sport has produced. The article appears to be a biographical profile highlighting his achievements and legacy in BJJ.", "score": 2, "rating": 1, "ratingReason": "The content presents Garcia in highly favorable terms, using superlative language like 'greatest pound-for-pound' and emphasizing his historical significance in the sport. The tone is clearly celebratory and respectful of his accomplishments.", "confidence": 0.7, "confidenceReason": "While the sentiment is clearly positive based on the language used, the limited content available (truncated article) and lack of specific details about his achievements or career milestones reduces confidence in providing a more complete sentiment analysis." }, "published": "Nov 12, 2024", "publishedMinutes": 98862, "image": "https://fb-serp-stream-feed-dev.s3.amazonaws.com/news/2025/01/19/11/dimg_MAGNZ7eJMuu9kPIPyPr5mAI_10.jpeg", "articleTitle": "Marcelo Garcia" } }, { "resulttype": "source-news-standard", "position": 0, "page": 1, "detailSection": { "desc": "Multi-time BJJ world champion Marcelo Garcia is ready to showcase his legendary skills in front of the global audience as he continues his...", "title": "Sportskeeda", "url": "http://www.sportskeeda.com/mma/news-marcelo-garcia-says-room-complacency-one-debut-vs-masakazu-imanari-somebody-s-training-beat-me", "urlid": 87465872, "markup": { "rating": -4, "auth": "crowd" }, "published": "2 days ago", "publishedMinutes": 2880, "image": "https://fb-serp-stream-feed-dev.s3.amazonaws.com/news/2025/01/19/11/dimg_MAGNZ7eJMuu9kPIPyPr5mAI_19.jpeg", "articleTitle": "Marcelo Garcia says there's no room for complacency in ONE debut vs Masakazu Imanari: \"Somebody's training to beat me\"" } }, { "resulttype": "source-news-standard", "position": 0, "page": 1, "detailSection": { "desc": "Marcelo Garcia is grinning from ear to ear as he prepares to do what he loves most once again at ONE 170 on Jan. 24.", "title": "Sportskeeda", "url": "http://www.sportskeeda.com/mma/news-given-much-jiu-jitsu-marcelo-garcia-says-learned-cope-aches-pains-bjj-career-gave", "urlid": 87465873, "markup": { "rating": -4, "auth": "crowd" }, "aimarkup": { "analysisKeyword":"OOGA", "summary": "Legendary BJJ practitioner Marcelo Garcia discusses managing physical challenges from his career while maintaining a positive outlook. Despite acknowledging the physical toll of jiu-jitsu, Garcia appears enthusiastic about returning to competition at ONE 170.", "score": 2, "rating": 1, "ratingReason": "While the article mentions physical 'aches and pains' from BJJ, Garcia's positive attitude, described as 'grinning from ear to ear' and his continued passion for the sport despite challenges presents an overall optimistic narrative. His resilience and ability to 'cope' with physical demands is framed as a triumph rather than a deterrent.", "confidence": 0.8, "confidenceReason": "The article content is brief but consistent in tone across title, description, and body. Garcia's direct quote and clear emotional state ('grinning from ear to ear') provide strong indicators of the positive sentiment, though the limited content depth slightly reduces confidence." }, "published": "2 days ago", "publishedMinutes": 2880, "image": "https://fb-serp-stream-feed-dev.s3.amazonaws.com/news/2025/01/19/11/dimg_MAGNZ7eJMuu9kPIPyPr5mAI_12.jpeg", "articleTitle": "â€œGiven so much to jiu-jitsuâ€ - Marcelo Garcia says he learned to cope with the aches and pains that his BJJ career gave him" } }, { "resulttype": "source-news-standard", "position": 0, "page": 1, "detailSection": { "desc": "41-year-old Brazilian jiu-jitsu icon Marcelo Garcia spent years away from competition because he felt fulfilled and wanted to shift his...", "title": "Sportskeeda", "url": "http://www.sportskeeda.com/mma/news-bjj-legend-marcelo-garcia-admits-initial-decision-step-away-competing-focus-teaching-coaching-students", "urlid": 87465874, "markup": { "rating": -4, "auth": "crowd" }, "aimarkup": { "analysisKeyword":"OOGA", "summary": "BJJ legend Marcelo Garcia revealed he stepped back from competition to dedicate himself to teaching and developing his students. The 41-year-old icon made this career transition after feeling fulfilled in his competitive career and wanting to focus on passing on his knowledge.", "score": 2, "rating": 1, "ratingReason": "The article presents a positive career transition where a respected martial arts figure chose to focus on teaching and mentoring others. This shows professional growth and commitment to developing the next generation, which aligns with positive community values.", "confidence": 0.7, "confidenceReason": "While the article headline and description provide clear information about Garcia's career decision, the limited content and lack of detailed quotes or context about the impact of this decision reduces full confidence in sentiment assessment." }, "published": "4 days ago", "publishedMinutes": 5760, "image": "https://fb-serp-stream-feed-dev.s3.amazonaws.com/news/2025/01/19/11/dimg_MAGNZ7eJMuu9kPIPyPr5mAI_5.jpeg", "articleTitle": "BJJ legend Marcelo Garcia admits his initial decision to step away from competing was to focus on teaching and coaching his students" } }, { "resulttype": "source-news-standard", "position": 0, "page": 1, "detailSection": { "desc": "BJJ legend Marcelo Garcia is set to make his return to competition later this month. It is a comeback being welcomed with open arms by...", "title": "Sportskeeda", "url": "http://www.sportskeeda.com/mma/news-be-marcelo-garcia-adriano-moraes-fans-nothing-love-bjj-legend-marcelo-garcia-s-return-action-one-170", "urlid": 87465875, "markup": { "rating": -4, "auth": "crowd" }, "aimarkup": { "analysisKeyword":"OOGA", "summary": "BJJ legend Marcelo Garcia is making a comeback to competitive action at ONE 170, generating widespread positive reception from fans and fellow fighters like Adriano Moraes. The announcement of Garcia's return has been met with enthusiasm and support from the combat sports community.", "score": 1, "rating": 1, "ratingReason": "The article conveys universal praise and excitement for Garcia's return, using phrases like 'nothing but love' and noting the warm welcome 'with open arms' from the community. The inclusion of support from prominent fighter Adriano Moraes adds to the positive sentiment.", "confidence": 0.8, "confidenceReason": "While the article content is limited, the consistent positive messaging across the title, description, and available content provides strong confidence in the sentiment. However, the abbreviated nature of the content prevents a perfect confidence score." }, "published": "1 day ago", "publishedMinutes": 1440, "image": "https://fb-serp-stream-feed-dev.s3.amazonaws.com/news/2025/01/19/11/dimg_MAGNZ7eJMuu9kPIPyPr5mAI_7.jpeg", "articleTitle": "â€œBe Marcelo Garciaâ€ - Adriano Moraes, fans have nothing but love for BJJ legend Marcelo Garciaâ€™s return to action at ONE 170" } }, { "resulttype": "source-news-standard", "position": 0, "page": 1, "detailSection": { "desc": "BJJ legend Marcelo Garcia is coming out of retirement to compete once again, and fans are all hyped up to see one of the best to ever do it...", "title": "Sportskeeda", "url": "http://www.sportskeeda.com/mma/news-the-original-goat-fighters-fans-alike-hyped-bjj-legend-marcelo-garcia-s-looming-return-action-one-170", "urlid": 87465876, "markup": { "rating": -4, "auth": "crowd" }, "aimarkup": { "analysisKeyword":"OOGA", "summary": "Legendary Brazilian Jiu-Jitsu practitioner Marcelo Garcia is announcing his return to competition at ONE 170 after being in retirement. The news has generated significant excitement among both professional fighters and fans, with Garcia being widely regarded as one of the greatest BJJ practitioners of all time.", "score": 1, "rating": 1, "ratingReason": "The article presents universally positive sentiment with terms like 'GOAT' (Greatest of All Time), 'legend', and describes widespread excitement ('hyped') from both fighters and fans about Garcia's return. There are no negative elements mentioned.", "confidence": 0.9, "confidenceReason": "While the article content is brief, the consistent positive terminology and unanimous excitement expressed across multiple groups (fighters and fans) provides strong confidence in the positive sentiment. The use of superlative terms ('GOAT', 'legend') and the focus on anticipation for his return reinforces this assessment." }, "published": "2 days ago", "publishedMinutes": 2880, "image": "https://fb-serp-stream-feed-dev.s3.amazonaws.com/news/2025/01/19/11/dimg_MAGNZ7eJMuu9kPIPyPr5mAI_1.jpeg", "articleTitle": "â€œThe Original GOATâ€ - Fighters and fans alike hyped for BJJ legend Marcelo Garciaâ€™s looming return to action at ONE 170" } }] }]
